import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { assetsAPI, jobsAPI, teamsAPI, teamMembersAPI, assetMaintenanceAPI, usersAPI } from '../utils/api';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currency';
import { format } from 'date-fns';
import JobSearchSelect from '../components/JobSearchSelect';
import {
  ArrowLeft,
  Package,
  Edit,
  Trash2,
  Save,
  X,
  Calendar,
  MapPin,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Wrench,
  Send,
  RotateCcw,
  History,
  User,
  Plus,
  FileText,
} from 'lucide-react';

const AssetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [asset, setAsset] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [jobs, setJobs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [maintenanceHistory, setMaintenanceHistory] = useState({ logs: [], total_cost: 0 });
  const [maintenanceData, setMaintenanceData] = useState({
    maintenance_type: 'routine',
    description: '',
    cost: 0,
    parts_used: '',
    performed_by: '',
    mileage_at_service: '',
    notes: '',
  });
  const [issueData, setIssueData] = useState({
    job_id: '',
    team_id: '',
    member_id: '',
    expected_return_date: '',
  });
  const [returnData, setReturnData] = useState({
    condition: 'good',
    notes: '',
  });

  const canEdit = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    loadAssetDetails();
    loadDropdownData();
    loadMaintenanceHistory();
  }, [id]);

  const loadAssetDetails = async () => {
    try {
      const response = await assetsAPI.getOne(id);
      setAsset(response.data);
      setEditData(response.data);
    } catch (error) {
      toast.error('Failed to load asset details');
      navigate('/assets');
    } finally {
      setLoading(false);
    }
  };

  const loadMaintenanceHistory = async () => {
    try {
      const response = await assetMaintenanceAPI.getMaintenanceHistory(id);
      setMaintenanceHistory(response.data);
    } catch (error) {
      console.error('Failed to load maintenance history');
    }
  };

  const loadDropdownData = async () => {
    try {
      const [jobsRes, teamsRes, membersRes, usersRes] = await Promise.all([
        jobsAPI.getAll(),
        teamsAPI.getAll(),
        teamMembersAPI.getAll(),
        usersAPI.getAll(),
      ]);
      // Only show jobs that are pending or in progress
      setJobs(jobsRes.data.filter(j => j.status !== 'completed'));
      setTeams(teamsRes.data);
      // Show active team members, or all team members, or fallback to users
      const activeMembers = membersRes.data.filter(m => m.status === 'active');
      if (activeMembers.length > 0) {
        setTeamMembers(activeMembers);
      } else if (membersRes.data.length > 0) {
        setTeamMembers(membersRes.data);
      } else {
        // Fallback to users if no team members exist
        setTeamMembers(usersRes.data.map(u => ({ id: u.id, name: u.name })));
      }
    } catch (error) {
      console.error('Failed to load dropdown data');
    }
  };

  const handleSaveEdit = async () => {
    try {
      await assetsAPI.update(id, {
        name: editData.name,
        asset_type: editData.asset_type,
        serial_number: editData.serial_number,
        condition: editData.condition,
        location: editData.location,
        notes: editData.notes,
      });
      setAsset({ ...asset, ...editData });
      setEditing(false);
      toast.success('Asset updated successfully');
    } catch (error) {
      toast.error('Failed to update asset');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;

    try {
      await assetsAPI.delete(id);
      toast.success('Asset deleted successfully');
      navigate('/assets');
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const handleIssueAsset = async () => {
    if (!issueData.job_id || !issueData.expected_return_date) {
      toast.error('Please select a job and expected return date');
      return;
    }

    if (!issueData.member_id) {
      toast.error('Please select a team member responsible for this asset');
      return;
    }

    try {
      await assetsAPI.issue({
        asset_id: id,
        job_id: issueData.job_id,
        team_id: issueData.team_id || null,
        member_id: issueData.member_id,
        expected_return_date: issueData.expected_return_date,
      });
      toast.success('Asset issued successfully');
      setShowIssueModal(false);
      setIssueData({ job_id: '', team_id: '', member_id: '', expected_return_date: '' });
      loadAssetDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to issue asset');
    }
  };

  const handleReturnAsset = async () => {
    try {
      await assetsAPI.return(id, {
        condition: returnData.condition,
        notes: returnData.notes || null,
      });
      toast.success('Asset returned successfully');
      setShowReturnModal(false);
      setReturnData({ condition: 'good', notes: '' });
      loadAssetDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return asset');
    }
  };

  const statusColors = {
    available: 'bg-green-100 text-green-800 border-green-200',
    issued: 'bg-blue-100 text-blue-700 border-blue-200',
    maintenance: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    retired: 'bg-slate-100 text-slate-700 border-slate-200',
  };

  const conditionColors = {
    good: 'bg-green-50 text-green-700 border-green-200',
    fair: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    poor: 'bg-red-50 text-red-700 border-red-200',
  };

  const maintenanceTypeColors = {
    routine: 'bg-blue-100 text-blue-700',
    repair: 'bg-orange-100 text-orange-700',
    inspection: 'bg-green-100 text-green-700',
    emergency: 'bg-red-100 text-red-700',
  };

  const handleAddMaintenance = async () => {
    if (!maintenanceData.description) {
      toast.error('Please provide a description');
      return;
    }
    try {
      await assetMaintenanceAPI.logMaintenance(id, {
        ...maintenanceData,
        cost: parseFloat(maintenanceData.cost) || 0,
        mileage_at_service: maintenanceData.mileage_at_service ? parseInt(maintenanceData.mileage_at_service) : null,
      });
      toast.success('Maintenance logged successfully');
      setShowMaintenanceModal(false);
      setMaintenanceData({
        maintenance_type: 'routine',
        description: '',
        cost: 0,
        parts_used: '',
        performed_by: '',
        mileage_at_service: '',
        notes: '',
      });
      loadAssetDetails();
      loadMaintenanceHistory();
    } catch (error) {
      toast.error('Failed to log maintenance');
    }
  };

  const isOverdue = asset?.status === 'issued' && 
    asset?.expected_return_date && 
    new Date(asset.expected_return_date) < new Date();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading asset details...</p>
        </div>
      </div>
    );
  }

  if (!asset) {
    return (
      <div className="p-6">
        <p className="text-slate-600">Asset not found</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate('/assets')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Assets
        </button>
        <div className="flex gap-2">
          {canEdit && !editing && (
            <>
              {asset.status === 'available' && (
                <button
                  onClick={() => setShowIssueModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Issue Asset
                </button>
              )}
              {asset.status === 'issued' && (
                <button
                  onClick={() => setShowReturnModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
                >
                  <RotateCcw className="w-4 h-4" />
                  Return Asset
                </button>
              )}
              <button
                onClick={() => setEditing(true)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
            </>
          )}
          {editing && (
            <>
              <button
                onClick={handleSaveEdit}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
              >
                <Save className="w-4 h-4" />
                Save
              </button>
              <button
                onClick={() => { setEditing(false); setEditData(asset); }}
                className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Asset Header */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-8">
            <div className="flex items-start gap-6">
              <div className="w-20 h-20 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Package className="w-10 h-10 text-blue-600" />
              </div>
              <div className="flex-1">
                {/* Asset Code */}
                <span className="text-sm font-mono font-bold text-blue-600 block mb-1">
                  {asset.asset_code || 'N/A'}
                </span>
                
                {editing ? (
                  <input
                    type="text"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="text-3xl font-bold text-slate-900 w-full border-b-2 border-blue-500 focus:outline-none pb-1 mb-2"
                  />
                ) : (
                  <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {asset.name}
                  </h1>
                )}
                
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  {editing ? (
                    <input
                      type="text"
                      value={editData.asset_type}
                      onChange={(e) => setEditData({ ...editData, asset_type: e.target.value })}
                      className="px-3 py-1 border rounded text-sm"
                      placeholder="Asset Type"
                    />
                  ) : (
                    <span className="text-slate-600">{asset.asset_type}</span>
                  )}
                  
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusColors[asset.status]}`}>
                    {asset.status === 'issued' && <Clock className="w-4 h-4 mr-1" />}
                    {asset.status === 'available' && <CheckCircle2 className="w-4 h-4 mr-1" />}
                    {asset.status === 'maintenance' && <Wrench className="w-4 h-4 mr-1" />}
                    {asset.status}
                  </span>
                  
                  {editing ? (
                    <select
                      value={editData.condition}
                      onChange={(e) => setEditData({ ...editData, condition: e.target.value })}
                      className="px-3 py-1 border rounded text-sm"
                    >
                      <option value="good">Good</option>
                      <option value="fair">Fair</option>
                      <option value="poor">Poor</option>
                    </select>
                  ) : (
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${conditionColors[asset.condition]}`}>
                      {asset.condition} condition
                    </span>
                  )}
                </div>

                {asset.serial_number && (
                  <div className="flex items-center gap-2 text-slate-600 mb-2">
                    <span className="font-medium">Serial Number:</span>
                    {editing ? (
                      <input
                        type="text"
                        value={editData.serial_number || ''}
                        onChange={(e) => setEditData({ ...editData, serial_number: e.target.value })}
                        className="px-2 py-1 border rounded text-sm font-mono"
                      />
                    ) : (
                      <span className="font-mono">{asset.serial_number}</span>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 text-slate-600">
                  <MapPin className="w-4 h-4" />
                  <span className="font-medium">Location:</span>
                  {editing ? (
                    <input
                      type="text"
                      value={editData.location}
                      onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                      className="px-2 py-1 border rounded text-sm"
                    />
                  ) : (
                    <span>{asset.location}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Overdue Warning */}
            {isOverdue && (
              <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0" />
                <div>
                  <p className="font-medium text-red-800">Asset Overdue!</p>
                  <p className="text-sm text-red-600">
                    Expected return was {format(new Date(asset.expected_return_date), 'MMM dd, yyyy')}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Issue Information (when issued) */}
          {asset.status === 'issued' && asset.issued_to_job && (
            <div className="bg-blue-50 border border-blue-200 shadow-sm rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                Currently Issued
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Issued to Job</p>
                  <p className="font-medium text-slate-900">
                    {jobs.find(j => j.id === asset.issued_to_job)?.title || asset.issued_to_job}
                  </p>
                  {jobs.find(j => j.id === asset.issued_to_job)?.job_code && (
                    <span className="text-xs font-mono text-blue-600">
                      {jobs.find(j => j.id === asset.issued_to_job)?.job_code}
                    </span>
                  )}
                </div>
                {asset.issued_to_team && (
                  <div>
                    <p className="text-sm text-slate-500">Issued to Team</p>
                    <p className="font-medium text-slate-900">
                      {teams.find(t => t.id === asset.issued_to_team)?.name || asset.issued_to_team}
                    </p>
                  </div>
                )}
                {asset.issued_to_member_name && (
                  <div>
                    <p className="text-sm text-slate-500">Responsible Person</p>
                    <p className="font-medium text-slate-900 flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-600" />
                      {asset.issued_to_member_name}
                    </p>
                  </div>
                )}
                {asset.issued_date && (
                  <div>
                    <p className="text-sm text-slate-500">Issued Date</p>
                    <p className="font-medium text-slate-900">
                      {format(new Date(asset.issued_date), 'MMM dd, yyyy')}
                    </p>
                  </div>
                )}
                {asset.expected_return_date && (
                  <div>
                    <p className="text-sm text-slate-500">Expected Return</p>
                    <p className={`font-medium ${isOverdue ? 'text-red-600' : 'text-slate-900'}`}>
                      {format(new Date(asset.expected_return_date), 'MMM dd, yyyy')}
                      {isOverdue && ' (OVERDUE)'}
                    </p>
                  </div>
                )}
                {asset.condition_on_issue && (
                  <div>
                    <p className="text-sm text-slate-500">Condition When Issued</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${conditionColors[asset.condition_on_issue]}`}>
                      {asset.condition_on_issue}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Return History (if returned with notes) */}
          {asset.status === 'available' && (asset.condition_on_return || asset.return_notes) && (
            <div className="bg-slate-50 border border-slate-200 shadow-sm rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
                <History className="w-5 h-5 text-slate-600" />
                Last Return Info
              </h2>
              <div className="space-y-3">
                {asset.condition_on_return && (
                  <div>
                    <p className="text-sm text-slate-500">Condition on Return</p>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${conditionColors[asset.condition_on_return]}`}>
                      {asset.condition_on_return}
                    </span>
                  </div>
                )}
                {asset.return_notes && (
                  <div>
                    <p className="text-sm text-slate-500">Return Notes</p>
                    <p className="text-slate-700">{asset.return_notes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Notes</h2>
            {editing ? (
              <textarea
                value={editData.notes || ''}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                rows={4}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Add notes about this asset..."
              />
            ) : (
              <p className="text-slate-600">{asset.notes || 'No notes added.'}</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Quick Stats */}
          <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Asset Details</h2>
            <div className="space-y-4">
              {asset.purchase_cost > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Purchase Cost</span>
                  <span className="font-semibold text-slate-900">{formatCurrency(asset.purchase_cost)}</span>
                </div>
              )}
              {asset.purchase_date && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-600">Purchase Date</span>
                  <span className="font-medium text-slate-900">
                    {format(new Date(asset.purchase_date), 'MMM dd, yyyy')}
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Created</span>
                <span className="font-medium text-slate-900">
                  {format(new Date(asset.created_at), 'MMM dd, yyyy')}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-slate-600">Last Updated</span>
                <span className="font-medium text-slate-900">
                  {format(new Date(asset.updated_at), 'MMM dd, yyyy')}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          {canEdit && (
            <div className="bg-white border border-slate-200 shadow-sm rounded-xl p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Actions</h2>
              <div className="space-y-2">
                {asset.status === 'available' && (
                  <button
                    onClick={() => setShowIssueModal(true)}
                    className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Issue to Job
                  </button>
                )}
                {asset.status === 'issued' && (
                  <button
                    onClick={() => setShowReturnModal(true)}
                    className="w-full py-2 px-4 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Return to Store
                  </button>
                )}
                <button
                  onClick={() => setShowMaintenanceModal(true)}
                  className="w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Wrench className="w-4 h-4" />
                  Log Maintenance
                </button>
                <button
                  onClick={handleDelete}
                  className="w-full py-2 px-4 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Asset
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Maintenance History Section */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <History className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Maintenance History</h2>
                <p className="text-sm text-slate-600">
                  {maintenanceHistory.total_records || 0} records • Total Cost: {formatCurrency(maintenanceHistory.total_cost || 0)}
                </p>
              </div>
            </div>
            {canEdit && (
              <button
                onClick={() => setShowMaintenanceModal(true)}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2 text-sm"
              >
                <Plus className="w-4 h-4" />
                Add Entry
              </button>
            )}
          </div>
          <div className="p-6">
            {maintenanceHistory.logs && maintenanceHistory.logs.length > 0 ? (
              <div className="space-y-4">
                {maintenanceHistory.logs.map((log) => (
                  <div key={log.id} className="border border-slate-200 rounded-lg p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${maintenanceTypeColors[log.maintenance_type] || 'bg-slate-100 text-slate-700'}`}>
                          {log.maintenance_type}
                        </span>
                        <span className="text-sm text-slate-500">
                          {log.maintenance_date ? format(new Date(log.maintenance_date), 'MMM dd, yyyy') : '-'}
                        </span>
                      </div>
                      <span className="font-semibold text-slate-900">{formatCurrency(log.cost || 0)}</span>
                    </div>
                    <p className="text-slate-800 font-medium mb-1">{log.description}</p>
                    {log.parts_used && (
                      <p className="text-sm text-slate-600 mb-1">
                        <span className="font-medium">Parts:</span> {log.parts_used}
                      </p>
                    )}
                    {log.mileage_at_service && (
                      <p className="text-sm text-slate-600 mb-1">
                        <span className="font-medium">Mileage:</span> {log.mileage_at_service.toLocaleString()} km
                      </p>
                    )}
                    {log.notes && (
                      <p className="text-sm text-slate-500 italic mt-2">{log.notes}</p>
                    )}
                    <div className="flex items-center gap-4 mt-3 pt-3 border-t border-slate-100 text-xs text-slate-500">
                      {log.performed_by_name && (
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" /> {log.performed_by_name}
                        </span>
                      )}
                      {log.created_by_name && (
                        <span>Logged by: {log.created_by_name}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Wrench className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p className="text-slate-500">No maintenance records yet</p>
                {canEdit && (
                  <button
                    onClick={() => setShowMaintenanceModal(true)}
                    className="mt-3 text-purple-600 hover:text-purple-700 font-medium text-sm"
                  >
                    Log first maintenance
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Issue Modal */}
      {showIssueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Send className="w-5 h-5 text-green-600" />
                Issue Asset
              </h2>
              <button onClick={() => setShowIssueModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Select Job *</label>
                <JobSearchSelect
                  jobs={jobs}
                  selectedJobId={issueData.job_id}
                  onSelect={(jobId) => setIssueData({ ...issueData, job_id: jobId })}
                  placeholder="Search for a job..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Select Team (Optional)</label>
                <select
                  value={issueData.team_id}
                  onChange={(e) => setIssueData({ ...issueData, team_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No specific team</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Responsible Team Member *
                </label>
                <select
                  value={issueData.member_id}
                  onChange={(e) => setIssueData({ ...issueData, member_id: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select responsible person...</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.name} {member.employee_id && `(${member.employee_id})`}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  This person is responsible for the asset during the job.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Expected Return Date *</label>
                <input
                  type="date"
                  value={issueData.expected_return_date}
                  onChange={(e) => setIssueData({ ...issueData, expected_return_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <strong>Current Condition:</strong> {asset.condition}
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  This condition will be recorded as the condition at time of issue.
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowIssueModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleIssueAsset}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Issue Asset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Return Modal */}
      {showReturnModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <RotateCcw className="w-5 h-5 text-orange-600" />
                Return Asset
              </h2>
              <button onClick={() => setShowReturnModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-sm text-slate-700">
                  <strong>Asset:</strong> {asset.name}
                </p>
                {asset.issued_to_member_name && (
                  <p className="text-sm text-slate-600 mt-1">
                    <strong>Returning from:</strong> {asset.issued_to_member_name}
                  </p>
                )}
                {asset.condition_on_issue && (
                  <p className="text-sm text-slate-600 mt-1">
                    <strong>Condition when issued:</strong> {asset.condition_on_issue}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Condition on Return *
                </label>
                <select
                  value={returnData.condition}
                  onChange={(e) => setReturnData({ ...returnData, condition: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="good">Good - No issues</option>
                  <option value="fair">Fair - Minor wear/damage</option>
                  <option value="poor">Poor - Significant damage/needs repair</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Return Notes
                  {returnData.condition !== 'good' && <span className="text-red-500 ml-1">*</span>}
                </label>
                <textarea
                  value={returnData.notes}
                  onChange={(e) => setReturnData({ ...returnData, notes: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Describe any damage, issues, or notes about the return..."
                />
                {returnData.condition !== 'good' && (
                  <p className="text-xs text-orange-600 mt-1">
                    Please provide details about the condition change.
                  </p>
                )}
              </div>

              {returnData.condition !== 'good' && asset.condition_on_issue === 'good' && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-orange-800 font-medium">Condition Changed</p>
                    <p className="text-xs text-orange-600">
                      The asset condition has degraded from &quot;{asset.condition_on_issue}&quot; to &quot;{returnData.condition}&quot;. 
                      Managers will be notified of this change.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowReturnModal(false)}
                  className="px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReturnAsset}
                  disabled={returnData.condition !== 'good' && !returnData.notes.trim()}
                  className="bg-orange-600 hover:bg-orange-700 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Return Asset
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Log Modal */}
      {showMaintenanceModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Wrench className="w-5 h-5 text-purple-600" />
                Log Maintenance
              </h2>
              <button onClick={() => setShowMaintenanceModal(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Maintenance Type *</label>
                <select
                  value={maintenanceData.maintenance_type}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, maintenance_type: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                >
                  <option value="routine">Routine Service</option>
                  <option value="repair">Repair</option>
                  <option value="inspection">Inspection</option>
                  <option value="emergency">Emergency Repair</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description *</label>
                <textarea
                  value={maintenanceData.description}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, description: e.target.value })}
                  placeholder="What maintenance was performed?"
                  rows={3}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Cost (R)
                  </label>
                  <input
                    type="text"
                    value={maintenanceData.cost}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9.]/g, '');
                      setMaintenanceData({ ...maintenanceData, cost: value });
                    }}
                    placeholder="0.00"
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Performed By</label>
                  <select
                    value={maintenanceData.performed_by}
                    onChange={(e) => setMaintenanceData({ ...maintenanceData, performed_by: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                  >
                    <option value="">Select...</option>
                    {teamMembers.map((member) => (
                      <option key={member.id} value={member.id}>{member.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Parts Used</label>
                <input
                  type="text"
                  value={maintenanceData.parts_used}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, parts_used: e.target.value })}
                  placeholder="e.g., Oil filter, brake pads"
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              {asset?.is_vehicle && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Mileage at Service (km)</label>
                  <input
                    type="text"
                    value={maintenanceData.mileage_at_service}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      setMaintenanceData({ ...maintenanceData, mileage_at_service: value });
                    }}
                    placeholder={asset?.vehicle_mileage ? `Current: ${asset.vehicle_mileage} km` : 'Enter mileage'}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  <FileText className="w-4 h-4 inline mr-1" />
                  Additional Notes
                </label>
                <textarea
                  value={maintenanceData.notes}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, notes: e.target.value })}
                  placeholder="Any additional notes..."
                  rows={2}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setShowMaintenanceModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddMaintenance}
                  disabled={!maintenanceData.description}
                  className="bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Wrench className="w-4 h-4" />
                  Log Maintenance
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetDetail;
